import { useUserLocaleOverride } from '@unifyapps/i18n/context/UserLocaleOverrideProvider';
import { fallbackLng } from '@unifyapps/i18n/settings';
import type { CurrentUserDetails } from '@unifyapps/network/generated/models/currentUserDetails';
import type { User } from '@unifyapps/network/generated/models/user';
import { getGetApiUserContextQueryKey } from '@unifyapps/network/generated/user-context-rest-api/user-context-rest-api';
import { useQueryClient } from '@unifyapps/network/react-query';
import _pick from 'lodash/pick';
import { useMemo } from 'react';
import { useUserStore } from '../../../auth/UserStoreProvider';
import { adaptUserExtraContextToDeviceContext } from '../../utils/deviceContextAdapters';

const KEYS_TO_PICK = [
  'id',
  'email',
  'name',
  'username',
  'contactDetails',
  'customProperties',
  'roles',
  'ssoLogin',
  'locale',
  'firstLogin',
];

export function getUserDetails(currentUser: User | undefined) {
  return currentUser ? { locale: fallbackLng, ..._pick(currentUser, KEYS_TO_PICK) } : undefined;
}

/**
 * Global hook to get the user context
 * @returns
 */
export function useGetUserContext() {
  const queryClient = useQueryClient();
  const currentUserDetails = useUserStore().hooks.useCurrentUserDetails();
  const currentUserRuntimeEnvDetails = useUserStore().hooks.useCurrentUserRuntimeEnvDetails();
  const { locale: localeOverride } = useUserLocaleOverride();

  const user = useMemo(() => {
    if (currentUserDetails?.user) return currentUserDetails.user;
    // adding a fallback to the queryClient in case the userStore is not yet populated
    // such that stores are initialized with the correct data
    return queryClient.getQueryData<CurrentUserDetails>(getGetApiUserContextQueryKey())?.user;
  }, [currentUserDetails?.user, queryClient]);

  const userDetails = useMemo(() => getUserDetails(user), [user]);

  const userContext = useMemo(() => {
    return {
      ...userDetails,
      ...(localeOverride ? { locale: localeOverride } : {}),
    };
  }, [userDetails, localeOverride]);

  // the runtime env is a separate first-class object now (`deviceContext` in the compute
  // context); the UserStore keeps the legacy server-exchange shape, adapted here.
  const deviceContext = useMemo(
    () => adaptUserExtraContextToDeviceContext(currentUserRuntimeEnvDetails),
    [currentUserRuntimeEnvDetails],
  );

  return { userContext, deviceContext };
}
